<template>
  <div class="bet-card">
    <!-- Header with Teams, Date and Time -->
    <div class="bet-card__header">
      <div class="league-row">
        <span class="league-name">{{ match.league }}</span>
        <!-- Match Scores -->
        <div class="bet-card__score" v-if="hasScore">
          <div class="score-full">{{ match.full_score }}</div>
          <!-- <div class="score-half">{{ match.half_score }}</div> -->
        </div>
        <span class="match-datetime">{{ formatDate(match.match_date) }} {{ formatTime(match.match_date) }}</span>
      </div>
      <div class="teams-row">
        <span class="team-name">{{ match.home_team.name }}</span>
        <span class="vs-text">VS</span>
        <span class="team-name">{{ match.away_team.name }}</span>
      </div>
    </div>

    <div class="bet-card__body">
      <!-- Handicap -->
      <div class="market-section">
        <div class="market-title">讓球盤 ({{ formatHandicap(match.first_odds ? match.first_odds.handicap_line : null) }})</div>
        <div class="odds-row odds-row--2">
          <div class="btn-odd-fixed" :class="{ active: isSelected('AH', 'HOME'), ...getStatusClass('AH', 'HOME') }">
            <span class="odd-label">主隊</span>
            <div v-if="isSelected('AH', 'HOME')" class="bet-indicator">
              <span class="payout" v-if="getPayout('AH', 'HOME') !== null">{{ getPayout('AH', 'HOME') }}</span>
              <span class="status-dot" :title="getStatusText('AH', 'HOME')"></span>
            </div>
            <span class="odd-val">{{ fmt(match.first_odds ? match.first_odds.handicap_home : null) }}</span>
          </div>
          <div class="btn-odd-fixed" :class="{ active: isSelected('AH', 'AWAY'), ...getStatusClass('AH', 'AWAY') }">
            <span class="odd-label">客隊</span>
            <div v-if="isSelected('AH', 'AWAY')" class="bet-indicator">
              <span class="payout" v-if="getPayout('AH', 'AWAY') !== null">{{ getPayout('AH', 'AWAY') }}</span>
              <span class="status-dot" :title="getStatusText('AH', 'AWAY')"></span>
            </div>
            <span class="odd-val">{{ fmt(match.first_odds ? match.first_odds.handicap_away : null) }}</span>
          </div>
        </div>
      </div>

      <!-- 1X2 -->
      <div class="market-section">
        <div class="market-title">標準盤 (1X2)</div>
        <div class="odds-row odds-row--3">
          <div class="btn-odd-fixed" :class="{ active: isSelected('1X2', 'HOME'), ...getStatusClass('1X2', 'HOME') }">
            <span class="odd-label">主勝</span>
            <div v-if="isSelected('1X2', 'HOME')" class="bet-indicator">
              <span class="payout" v-if="getPayout('1X2', 'HOME') !== null">{{ getPayout('1X2', 'HOME') }}</span>
            </div>
            <span class="odd-val">{{ fmt(match.first_odds ? match.first_odds.odds_home_win : null) }}</span>
          </div>
          <div class="btn-odd-fixed" :class="{ active: isSelected('1X2', 'DRAW'), ...getStatusClass('1X2', 'DRAW') }">
            <span class="odd-label">和局</span>
            <div v-if="isSelected('1X2', 'DRAW')" class="bet-indicator">
              <span class="payout" v-if="getPayout('1X2', 'DRAW') !== null">{{ getPayout('1X2', 'DRAW') }}</span>
            </div>
            <span class="odd-val">{{ fmt(match.first_odds ? match.first_odds.odds_draw : null) }}</span>
          </div>
          <div class="btn-odd-fixed" :class="{ active: isSelected('1X2', 'AWAY'), ...getStatusClass('1X2', 'AWAY') }">
            <span class="odd-label">客勝</span>
            <div v-if="isSelected('1X2', 'AWAY')" class="bet-indicator">
              <span class="payout" v-if="getPayout('1X2', 'AWAY') !== null">{{ getPayout('1X2', 'AWAY') }}</span>
            </div>
            <span class="odd-val">{{ fmt(match.first_odds ? match.first_odds.odds_away_win : null) }}</span>
          </div>
        </div>
      </div>

      <!-- OU -->
      <div class="market-section">
        <div class="market-title">大小球 ({{ formatGoalsLine(match.first_odds ? match.first_odds.goals_line : null) }})</div>
        <div class="odds-row odds-row--2">
          <div class="btn-odd-fixed" :class="{ active: isSelected('OU', 'OVER'), ...getStatusClass('OU', 'OVER') }">
            <span class="odd-label">大球</span>
            <div v-if="isSelected('OU', 'OVER')" class="bet-indicator">
              <span class="payout" v-if="getPayout('OU', 'OVER') !== null">{{ getPayout('OU', 'OVER') }}</span>
            </div>
            <span class="odd-val">{{ fmt(match.first_odds ? match.first_odds.goals_over : null) }}</span>
          </div>
          <div class="btn-odd-fixed" :class="{ active: isSelected('OU', 'UNDER'), ...getStatusClass('OU', 'UNDER') }">
            <span class="odd-label">小球</span>
            <div v-if="isSelected('OU', 'UNDER')" class="bet-indicator">
              <span class="payout" v-if="getPayout('OU', 'UNDER') !== null">{{ getPayout('OU', 'UNDER') }}</span>
            </div>
            <span class="odd-val">{{ fmt(match.first_odds ? match.first_odds.goals_under : null) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatHandicap, formatGoalsLine } from '@/utils/odds'
import { parseMatchTime, parseMatchDate } from '@/utils/time'

export default {
  name: 'BetCard',
  props: {
    match: { type: Object, required: true },
    bets: { type: Array, required: true } // Array of bet objects
  },
  computed: {
    hasScore() {
      return !!(this.match.full_score || this.match.half_score)
    }
  },
  methods: {
    isSelected(marketId, selectionId) {
      return this.bets.some(b => b && b.market_id === marketId && b.selection_id === selectionId)
    },
    getBet(marketId, selectionId) {
      return this.bets.find(b => b && b.market_id === marketId && b.selection_id === selectionId)
    },
    getPayout(marketId, selectionId) {
      const b = this.getBet(marketId, selectionId)
      return b ? b.payout : null
    },
    getStatusText(marketId, selectionId) {
       const b = this.getBet(marketId, selectionId)
       if (!b) return ''
       const m = { PENDING: '未開獎', WIN: '贏', LOSE: '輸', VOID: '退本', HALF_WIN: '贏半', HALF_LOSE: '輸半' }
       // If Payout > Stake, it's definitely a win. If Payout == Stake, it's VOID.
       if (b.status === 'WIN' && b.payout === b.stake) return '退本'
       return m[b.status] || b.status
    },
    getStatusClass(marketId, selectionId) {
      const b = this.getBet(marketId, selectionId)
      if (!b) return {}
      return {
        [`status--${b.status.toLowerCase()}`]: true
      }
    },
    formatTime(dateStr) {
      return parseMatchTime(dateStr)
    },
    formatDate(dateStr) {
      return parseMatchDate(dateStr)
    },
    fmt(val) {
      if (val === null || val === undefined) return '—'
      const n = Number(val)
      if (isNaN(n)) return val
      return n % 1 === 0 ? String(n) : n.toFixed(2).replace(/0+$/, '')
    },
    formatHandicap,
    formatGoalsLine
  }
}
</script>

<style scoped>
.bet-card {
  background: var(--bg-card);
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
  transition: background var(--transition-fast);
}

.bet-card:last-child {
  border-bottom: none;
}

.bet-card:hover {
  background: var(--bg-card-hover);
}

.bet-card__header {
  border-bottom: 1px solid var(--border);
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.league-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.league-name {
  font-size: 13px;
  color: var(--primary-light);
  font-weight: 700;
}
.match-datetime {
  font-size: 12px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

.teams-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
}
.team-name {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}
.vs-text {
  font-size: 12px;
  font-weight: 800;
  color: var(--text-muted);
}

.bet-card__score {
  text-align: center;
}
.score-full {
  font-size: 16px;
  font-weight: 800;
  color: var(--score-full);
  line-height: 1.1;
}
.score-half {
  font-size: 11px;
  color: var(--score-half);
  margin-top: 1px;
}

.bet-card__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.market-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.market-title {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 600;
}

.odds-row {
  display: grid;
  gap: 8px;
}
.odds-row--2 { grid-template-columns: 1fr 1fr; }
.odds-row--3 { grid-template-columns: 1fr 1fr 1fr; }

.btn-odd-fixed {
  background: var(--bg-body);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-secondary);
  position: relative;
  overflow: hidden;
}

.btn-odd-fixed.active {
  background: #FF8C00;
  border-color: #FF8C00;
  color: #fff;
  box-shadow: 0 2px 8px rgba(255, 140, 0, 0.3);
}

.btn-odd-fixed.active .odd-label { color: rgba(255,255,255,0.8); }
.btn-odd-fixed.active .odd-val { color: #fff; }

.odd-label { font-size: 11px; }
.odd-val { font-size: 13px; font-weight: 700; font-variant-numeric: tabular-nums; }

.bet-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
}

.payout {
  font-size: 11px;
  font-weight: 800;
  background: rgba(0,0,0,0.2);
  padding: 0 4px;
  border-radius: 4px;
}

/* Status colors */
.status--win.active { background-color: var(--score-full); border-color: var(--score-full); box-shadow: 0 2px 8px rgba(71, 161, 93, 0.3); }
.status--half_win.active { background-color: var(--score-full); border-color: var(--score-full); opacity: 0.9; }
.status--lose.active { background-color: #E53935; border-color: #E53935; box-shadow: 0 2px 8px rgba(229, 57, 53, 0.3); }
.status--half_lose.active { background-color: #E53935; border-color: #E53935; opacity: 0.9; }
.status--void.active { background-color: var(--text-muted); border-color: var(--text-muted); }

</style>
